import { createContext, useState, useCallback, useContext, useEffect, useMemo, useRef, type MutableRefObject } from "react";
import { useLocation } from "react-router-dom";
import { Gift } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { usePersonalizationPreferences } from "@/hooks/usePersonalizationPreferences";
import { useAuth } from "@/hooks/useAuth";
import { useFeatureGate } from "@/hooks/useTierGate";
import { isWelcomeFlowSuppressedRoute } from "@/components/layout/AuthenticatedProviders";
import { isPublicSurfaceRoute } from "@/ui/app-shell/public-surface";
import { ONBOARDING_DISMISSED_KEY } from "@/components/onboarding/WelcomeFlow";
import {
  isAppLayoutMounted,
  isAppLayoutChangelogEntryPointMounted,
  subscribeAppLayoutChangelogEntryPointMounted,
  subscribeAppLayoutMounted,
  isAdminLayoutMounted,
  subscribeAdminLayoutMounted,
} from "@/components/layout/AppLayoutMountSignal";
import { CHANGELOG_ENTRIES, isChangelogEntryVisible, isChangelogIdNewer } from "@/data/changelog";
import { useRuntimeFlags } from "@/contexts/FeatureFlagContext";
import { cn } from "@/lib/utils";

/** Routes that render a standalone, unattended or lockout-style fullscreen
 * surface — confirmed by reading their route element in src/App.tsx. Unlike
 * useAppLayoutMounted below (which only hides the feedback CTA while still
 * showing the rest of the modal), these need the WHOLE modal suppressed:
 * /events/check-in/kiosk is a self-service check-in station meant to run
 * fullscreen on a tablet at a church entrance with nobody staffing it, and an
 * internal "what's new" modal popping over it would interrupt real-world
 * attendee check-in. Does NOT include /events/check-in (the staff-operated,
 * non-kiosk check-in route) — that one hasn't been confirmed to need the same
 * treatment. /disabled is where ProtectedRoute.tsx redirects a member of a
 * disabled organization (a distinct route from /account-disabled, which
 * isWelcomeFlowSuppressedRoute already covers) — a lockout notice is not the
 * place for a product announcement either. */
const FULLSCREEN_SUPPRESSED_ROUTES = ["/events/check-in/kiosk", "/disabled"];

/** Strips a single trailing slash from a pathname, except the root path `/`
 * itself, which stays `/`. React Router can match a URL with a trailing
 * slash (e.g. `/events/check-in/kiosk/`) to the same route/component as its
 * bare form, but a plain `.includes()` check against the list above would
 * miss it — letting a trailing slash silently bypass suppression on exactly
 * the surfaces it exists to protect. Normalize before every exact-match
 * check against it. */
function stripTrailingSlash(pathname: string): string {
  return pathname.length > 1 && pathname.endsWith("/") ? pathname.slice(0, -1) : pathname;
}

/** Whether an `AppLayout` instance is currently mounted anywhere in the tree.
 *
 * AppLayout owns the only `open-solomon` listener and the only mount of
 * `<SolomonPanel>` (see AppLayout.tsx). ChangelogModal is mounted once, at
 * the App.tsx route-wrapper level — a sibling of whatever each route renders,
 * not a descendant of AppLayout — so it can't reach AppLayout via React
 * context and has no reliable way to know, from `location.pathname` alone,
 * whether the current route happens to render AppLayout: react-router routes
 * that do (the large majority) don't record that fact anywhere inspectable,
 * and routes that don't are discovered one at a time by review (three so far
 * across separate review rounds — /my/sermons, /my/giving, every /admin/*
 * page, and the wildcard 404 route — with no way to know how many more exist
 * or will be added later). A hand-maintained list of exceptions can only
 * ever cover exceptions already found; this hook asks the real question
 * instead — "is AppLayout actually mounted right now" — via
 * AppLayoutMountSignal.ts, so it's correct for any route, including ones
 * nobody has reviewed yet.
 *
 * Reads the CURRENT state synchronously (via isAppLayoutMounted()), not just
 * future transitions (via subscribeAppLayoutMounted()) — a plain "listen for
 * the next event" approach misses AppLayout instances that were already
 * mounted from an earlier commit. That's reachable whenever ChangelogModal
 * mounts fresh while an already-mounted AppLayout instance doesn't (e.g. the
 * global error boundary resets while auth/org state stays hydrated, so only
 * this component remounts): there is no new "mounted" transition to catch,
 * because AppLayout never stopped being mounted. See AppLayoutMountSignal.ts
 * for the full reasoning. */
function useMountSignal(
  isMounted: () => boolean,
  subscribe: (listener: (mounted: boolean) => void) => () => void,
): boolean {
  const [mounted, setMounted] = useState(isMounted);

  useEffect(() => {
    // Re-sync in case the true state changed between the lazy useState
    // initializer above (which runs during render, before any effects) and
    // this effect running.
    setMounted(isMounted());
    return subscribe(setMounted);
  }, [isMounted, subscribe]);

  return mounted;
}

function useAppLayoutMounted(): boolean {
  return useMountSignal(isAppLayoutMounted, subscribeAppLayoutMounted);
}

/** Whether an `AdminLayout` instance is currently mounted anywhere in the
 * tree — the AdminLayout counterpart to useAppLayoutMounted above, backed by
 * its own independent signal (AdminLayout doesn't mount the Solomon panel,
 * so it must not affect canShareFeedback below; see AppLayoutMountSignal.ts).
 * AdminLayout renders its own ChangelogEntryPointButton, so this exists to
 * let the auto-popup gate recognize that shell as a valid reopen path too
 * (Codex P2, PR #6347). */
function useAdminLayoutMounted(): boolean {
  return useMountSignal(isAdminLayoutMounted, subscribeAdminLayoutMounted);
}

interface ChangelogContextValue {
  open: boolean;
  setOpen: (open: boolean) => void;
  menuReturnFocus: MutableRefObject<HTMLElement | null>;
  buttonReturnFocus: MutableRefObject<HTMLButtonElement | null>;
}

const ChangelogContext = createContext<ChangelogContextValue | null>(null);

/** Wraps the part of the tree that mounts <ChangelogModal /> so its open
 * state is reachable from the persistent top-bar entry point — mirrors
 * WelcomeFlowProvider (src/components/onboarding/WelcomeFlow.tsx) for the
 * same reason: a modal-on-load pattern needs an external "open on demand"
 * path in addition to its own auto-popup gate. */
export function ChangelogProvider({ children }: { children: React.ReactNode }) {
  const [open, setOpen] = useState(false);
  const menuReturnFocus = useRef<HTMLElement | null>(null);
  const buttonReturnFocus = useRef<HTMLButtonElement | null>(null);
  return (
    <ChangelogContext.Provider value={{ open, setOpen, menuReturnFocus, buttonReturnFocus }}>
      {children}
    </ChangelogContext.Provider>
  );
}

function useChangelogContext() {
  const ctx = useContext(ChangelogContext);
  if (!ctx) {
    throw new Error("Changelog components must be rendered within a ChangelogProvider");
  }
  return ctx;
}

/** Opens the changelog modal on demand, independent of the unseen-entry
 * auto-popup gate. Used by ChangelogEntryPointButton. */
export function useOpenChangelog() {
  const { setOpen } = useChangelogContext();
  return useCallback(() => setOpen(true), [setOpen]);
}

// Flag-gated entries (ChangelogEntry.requiresFlag) can switch on long after newer entries were
// seen, so "last seen id" can't announce them. Each browser keeps, per organization/user scope,
// the gated ids it has shown. Per-browser by design: at worst a gated entry is announced once
// more on another device, with no server change.
const GATED_SEEN_EVENT = "forge:changelog-gated-seen";
const gatedSeenKey = (scopeKey: string) => `changelog-seen-gated:${scopeKey}`;

function readGatedSeen(scopeKey: string | null): Set<string> {
  if (!scopeKey) return new Set();
  try {
    const parsed: unknown = JSON.parse(window.localStorage.getItem(gatedSeenKey(scopeKey)) ?? "[]");
    return new Set(Array.isArray(parsed) ? parsed.filter((id): id is string => typeof id === "string") : []);
  } catch {
    return new Set();
  }
}

function markGatedSeen(scopeKey: string, ids: string[]) {
  if (ids.length === 0) return;
  try {
    const next = readGatedSeen(scopeKey);
    ids.forEach((id) => next.add(id));
    window.localStorage.setItem(gatedSeenKey(scopeKey), JSON.stringify([...next]));
  } catch {
    // Storage unavailable: the entry may be announced again, never lost.
  }
  window.dispatchEvent(new Event(GATED_SEEN_EVENT));
}

const NO_FLAGS = {};

/** Announce-once entries: flag-gated ones, and formerly gated ones kept on this path with
 * `announceOnce` after their gate is dropped. Their id order can't decide "seen", so each
 * browser remembers them individually. */
const isAnnouncedOnce = (entry: (typeof CHANGELOG_ENTRIES)[number]) => !!entry.requiresFlag || !!entry.announceOnce;

/** What's New as this viewer sees it: entries whose flag (if any) is on for their organization,
 * the newest visible id, and whether a visible flag-gated entry hasn't been shown here yet. */
function useVisibleChangelog(scopeKey: string | null) {
  const { flags: runtimeFlags, isCurrentForOrganization } = useRuntimeFlags();
  // Until this organization's own flags arrive, reveal no gated entry: after an org switch the
  // provider still holds the previous org's map, and acting on it could show (and mark seen)
  // an entry that isn't available here.
  const flags = isCurrentForOrganization ? runtimeFlags : NO_FLAGS;
  const [seenGated, setSeenGated] = useState(() => readGatedSeen(scopeKey));
  useEffect(() => {
    const reload = () => setSeenGated(readGatedSeen(scopeKey));
    reload();
    window.addEventListener(GATED_SEEN_EVENT, reload);
    window.addEventListener("storage", reload);
    return () => {
      window.removeEventListener(GATED_SEEN_EVENT, reload);
      window.removeEventListener("storage", reload);
    };
  }, [scopeKey]);
  return useMemo(() => {
    const entries = CHANGELOG_ENTRIES.filter((entry) => isChangelogEntryVisible(entry, flags));
    const gatedIds = entries.filter(isAnnouncedOnce).map((entry) => entry.id);
    const isUnseenGated = (entry: (typeof entries)[number]) => isAnnouncedOnce(entry) && !seenGated.has(entry.id);
    return {
      // A gated entry keeps the id of the day its PR merged, so by the time its flag turns on
      // it can sit below newer entries. Lead with it so the announcement is what's read first.
      entries: [...entries.filter(isUnseenGated), ...entries.filter((entry) => !isUnseenGated(entry))],
      latestId: entries[0]?.id,
      gatedIds,
      hasUnseenGated: entries.some(isUnseenGated),
    };
  }, [flags, seenGated]);
}

/** Whether `latestId` counts as unseen relative to `lastSeenId`. Shared by
 * ChangelogModal (auto-popup gate) and ChangelogEntryPointButton (badge) so
 * they can't disagree — they briefly did, when a prior fix to this
 * comparison only landed in one of the two copies.
 *
 * Direction-aware, not a bare !== : a user can run an OLDER client build
 * than the one that last wrote lastSeenId — e.g. a stale browser tab, or a
 * native app a beat behind a newer deploy — after a different device
 * already recorded a newer id there. Only treat the entry as unseen when
 * latestId is actually newer than what's stored (via isChangelogIdNewer,
 * @/data/changelog — NOT a raw `>` string comparison, which breaks once a
 * same-day suffix reaches double digits); !== would also fire when it's
 * OLDER, incorrectly announcing already-superseded release notes on the
 * stale client. */
function isEntryUnseen(latestId: string | undefined, lastSeenId: string | undefined): boolean {
  return latestId !== undefined && (lastSeenId === undefined || isChangelogIdNewer(latestId, lastSeenId));
}

export function ChangelogModal() {
  const { open, setOpen, menuReturnFocus, buttonReturnFocus } = useChangelogContext();
  const location = useLocation();
  const { user } = useAuth();
  const {
    preferences,
    setLastSeenChangelogId,
    isReady: preferencesReady,
    scopeKey,
  } = usePersonalizationPreferences();
  const visibleChangelog = useVisibleChangelog(scopeKey);
  const { hasFeature: solomonEnabled } = useFeatureGate("solomon_ai");
  const appLayoutMounted = useAppLayoutMounted();
  const adminLayoutMounted = useAdminLayoutMounted();
  const appLayoutChangelogEntryPointMounted = useMountSignal(
    isAppLayoutChangelogEntryPointMounted,
    subscribeAppLayoutChangelogEntryPointMounted,
  );
  // Standard shell chrome gives the auto-popup a working reopen path. A
  // chrome-free AppLayout still mounts Solomon but omits the entry point.
  // Only AppLayout mounts the Solomon panel
  // — canShareFeedback below stays scoped to appLayoutMounted alone.
  const hasChangelogEntryPoint = appLayoutChangelogEntryPointMounted || adminLayoutMounted;

  // Normalized once per render and reused by the exact-match check below
  // against FULLSCREEN_SUPPRESSED_ROUTES — see stripTrailingSlash above. The
  // imported isWelcomeFlowSuppressedRoute/isPublicSurfaceRoute checks below
  // use the raw, un-normalized location.pathname deliberately: they're
  // existing utilities this feature didn't write and doesn't own.
  const normalizedPathname = stripTrailingSlash(location.pathname);

  // Suppresses both the "close if already open" effect below and the
  // auto-popup effect. Covers the login/reset/MFA routes WelcomeFlow already
  // suppresses on, plus public-facing surfaces (e.g. /b/:id, the public
  // bulletin viewer) that isWelcomeFlowSuppressedRoute doesn't know about —
  // an authenticated staff member can land there with an unseen changelog
  // entry, and the modal shouldn't pop over a page meant for public viewers —
  // plus the unattended kiosk and account-lockout fullscreen routes, where no
  // internal staff-facing modal should ever appear (see
  // FULLSCREEN_SUPPRESSED_ROUTES above) — plus `scopeKey === null`, meaning
  // the current user has no organization at all (checked directly, not via a
  // route list: usePersonalizationPreferences scopes its state to
  // `${orgId}:${userId}`, and with no org this feature's own persistence
  // can't work — every write, including this modal's "mark seen" call, would
  // silently no-op — see usePersonalizationPreferences.tsx's ensureScope()/
  // applyUpdate()). A route list here would only ever catch org-less routes
  // already found by review — /onboarding is the obvious one, but a platform
  // super admin can also reach /admin/* with no organization at all
  // (ProtectedRoute.tsx permits it), which a route list scoped to
  // /onboarding alone would miss entirely. Checking scopeKey directly is
  // correct for both and any future case.
  const isSuppressedRoute =
    isWelcomeFlowSuppressedRoute(location.pathname) ||
    isPublicSurfaceRoute(location.pathname) ||
    FULLSCREEN_SUPPRESSED_ROUTES.includes(normalizedPathname) ||
    scopeKey === null;
  const hasUnseen =
    isEntryUnseen(visibleChangelog.latestId, preferences.lastSeenChangelogId) || visibleChangelog.hasUnseenGated;

  // Mirrors WelcomeFlow's own "onboarding is done" determination: WelcomeFlow
  // treats onboarding as handled when EITHER the server-side welcomeCompleted
  // flag is true OR the legacy client-only ONBOARDING_DISMISSED_KEY is present
  // in localStorage (see WelcomeFlow.tsx's auto-popup gate). A user who
  // dismissed onboarding back when only that localStorage key was written has
  // welcomeCompleted stuck at false forever server-side — persistDismiss()
  // only sets it when the modal is actually shown and dismissed — so relying
  // on preferences.welcomeCompleted alone here would permanently block the
  // changelog auto-popup for that user even though WelcomeFlow itself
  // considers their onboarding fully handled.
  const isOnboardingHandled = (() => {
    if (preferences.welcomeCompleted) return true;
    try {
      return localStorage.getItem(ONBOARDING_DISMISSED_KEY) !== null;
    } catch {
      // localStorage unavailable
      return false;
    }
  })();

  // Tracks whether the most recent open->closed transition was a forced
  // suppression closure (the effect right below, firing because navigation
  // or an auth/org redirect landed on a suppressed surface while the modal
  // happened to be open) rather than something the user actually did — the
  // X button, click-outside, Escape, or the Share Feedback action. Read by
  // the transition-watcher effect further down: only a user-driven close
  // should count as "the user viewed this and is done with it." A forced
  // closure can happen without the user ever having seen the dialog at all
  // (e.g. it auto-popped in a background tab, then a redirect force-closed
  // it before the tab was ever brought to front) and must not mark the
  // entry seen.
  const forcedCloseRef = useRef(false);

  // Tracks the PREVIOUS value of hasChangelogEntryPoint so the effect below
  // can force-close only on a genuine true -> false transition (a shell that
  // WAS mounted has now gone away), never merely because it reads false on
  // the render where the dialog first opens. That "false at open time" case
  // is a real, legitimate state: ChangelogEntryPointButton always renders as
  // a child of AppLayout/AdminLayout, so a click on it always happens from
  // inside one of those shells — hasChangelogEntryPoint reading false there
  // is only ever a brief "mount effect hasn't confirmed yet" race, not an
  // absent shell, and force-closing on it would close the dialog the instant
  // it opens. Initialized to the CURRENT value (not `false`) so a component
  // that mounts fresh while a shell is already mounted doesn't read a
  // spurious true -> false transition on its very first effect run.
  const hadChangelogEntryPointRef = useRef(hasChangelogEntryPoint);

  // Captures which preferences scope (org+user) was active at the moment
  // the dialog transitioned from closed to open — set below, in the
  // wasOpenRef-tracking effect further down. Read here so a LATER scope
  // change while still open (e.g. background membership/tenant-context
  // reconciliation) can be force-closed before any write happens (Codex P2,
  // PR #6347, round 6): the release notes themselves aren't org-specific,
  // but lastSeenChangelogId IS scoped per-org, so marking the wrong org's
  // preferences seen would leave the org the user actually read it in still
  // flagged unseen, while a different org silently gets marked seen for
  // content the user never opened the modal under. `null` means "no scope
  // has been captured for the current open session yet" (either the dialog
  // has never opened, or this is the very same render where it just did,
  // before the capture effect below has run) — guarded against below so a
  // dialog's very first open of the session doesn't itself read as a scope
  // mismatch against this ref's uninitialized `null`.
  const openedScopeKeyRef = useRef<string | null>(null);

  // Close immediately if the user lands on a public/auth route while open —
  // same reasoning as WelcomeFlow: avoids popping over a login/reset screen —
  // OR if a shell that WAS mounted while the dialog opened has since gone
  // away entirely (Codex P2, PR #6347, round 5): browser-history navigation
  // (e.g. back/forward) can carry an already-open dialog from an
  // AppLayout/AdminLayout page onto a route like /my/sermons without ever
  // closing it first — that route isn't suppressed, so it would otherwise
  // sit open indefinitely, and a subsequent dismissal would mark the entry
  // seen from a route with no reopen path if the user ever wants to look
  // again — OR if the active preferences scope has changed since the dialog
  // opened (Codex P2, PR #6347, round 6, see openedScopeKeyRef above).
  // Treating every one of these the same as landing on a suppressed route
  // keeps the round-2 "you can always reopen this" guarantee, and the
  // round-6 "never mark the wrong org's changelog seen" guarantee, airtight
  // regardless of how the dialog came to be open.
  useEffect(() => {
    const hadEntryPoint = hadChangelogEntryPointRef.current;
    hadChangelogEntryPointRef.current = hasChangelogEntryPoint;

    if (isSuppressedRoute && open) {
      forcedCloseRef.current = true;
      setOpen(false);
      return;
    }
    if (hadEntryPoint && !hasChangelogEntryPoint && open) {
      forcedCloseRef.current = true;
      setOpen(false);
      return;
    }
    if (openedScopeKeyRef.current !== null && openedScopeKeyRef.current !== scopeKey && open) {
      forcedCloseRef.current = true;
      setOpen(false);
    }
  }, [isSuppressedRoute, hasChangelogEntryPoint, scopeKey, open, setOpen]);

  // Auto-popup once per user when there's an unseen entry. Unlike WelcomeFlow
  // this needs no separate localStorage check — usePersonalizationPreferences
  // already mirrors lastSeenChangelogId to localStorage as part of its own
  // dual-write, so the preferences hook is the single source of truth here.
  useEffect(() => {
    if (!user) return;
    if (!preferencesReady) return;
    if (isSuppressedRoute) return;
    if (!hasUnseen) return;
    // Codex P2, PR #6347 (dev -> main promotion, round 2): the persistent
    // top-bar entry point (ChangelogEntryPointButton, this modal's only
    // reopen path) lives inside AppLayout OR AdminLayout — a route that
    // mounts NEITHER (e.g. /my/sermons) has no way to reopen this modal once
    // dismissed, contradicting the release-note copy that tells the user
    // they can. Rather than hand-maintain a route list here too (the exact
    // maintenance burden useAppLayoutMounted's live check above already
    // exists to avoid for the Share Feedback CTA), defer the auto-popup
    // itself until a shell with a working reopen path is actually mounted --
    // the user sees it on their next visit to a route that has one, instead
    // of on a route where dismissing it would be a dead end.
    //
    // Round 4: gating on appLayoutMounted alone regressed this for
    // AdminLayout specifically, once round 2 also gave it its own
    // ChangelogEntryPointButton (below) -- an admin who only ever visits
    // /admin/* routes has a perfectly good reopen path but would never get
    // the auto-popup, since AdminLayout never mounts AppLayout itself. Use
    // the combined hasChangelogEntryPoint (which OR's in AdminLayout's own,
    // Solomon-independent mount signal) instead, so this gate tracks "is
    // SOME supported shell mounted" while canShareFeedback below stays
    // scoped to AppLayout specifically (the only shell with a Solomon panel).
    if (!hasChangelogEntryPoint) return;
    // Defer the changelog's own auto-popup until onboarding is complete —
    // WelcomeFlow schedules its auto-popup on the same 1s delay for a
    // brand-new user (welcomeCompleted === false, no lastSeenChangelogId),
    // which without this guard opens two Radix dialogs at once. Use the
    // combined isOnboardingHandled check (not the bare welcomeCompleted flag)
    // so a user who dismissed onboarding under the old localStorage-only
    // behavior — whose welcomeCompleted can be stuck at false server-side
    // forever — still gets the changelog popup, matching WelcomeFlow's own
    // view that their onboarding is done. This only defers the popup on a
    // user's genuine first visit — anyone whose onboarding is handled by
    // either signal still gets the auto-popup normally.
    if (!isOnboardingHandled) return;

    const timer = setTimeout(() => setOpen(true), 1000);
    return () => clearTimeout(timer);
  }, [user, preferencesReady, isSuppressedRoute, hasUnseen, hasChangelogEntryPoint, isOnboardingHandled, setOpen]);

  // Marks the newest entry seen once the user actually closes the modal —
  // NOT when it opens. Marking on open meant the 1-second auto-popup timer
  // (which browsers still run, throttled, in a backgrounded tab) could flip
  // `open` to true and clear the "unseen" badge before the user ever
  // actually saw the dialog, if they switched away or closed the tab in that
  // window. This matches the original design intent (see
  // docs/superpowers/specs/2026-09-13-changelog-rollup-design.md section 3.4:
  // "(on view) setLastSeenChangelogId(latest)") and mirrors how WelcomeFlow
  // marks welcomeCompleted from persistDismiss() on every exit path, never
  // on open.
  //
  // `justClosed` tracks the open->closed transition directly (via a ref of
  // the previous `open` value) rather than hooking only Dialog's
  // onOpenChange below, because the modal has more than one way to close:
  // onOpenChange covers the built-in X button and click-outside/Escape, but
  // handleShareFeedback (below) closes the modal with a direct
  // `setOpen(false)` that never goes through onOpenChange. Watching `open`
  // itself catches every path uniformly.
  const wasOpenRef = useRef(false);
  const [justClosed, setJustClosed] = useState(false);

  // Captures which preferences scope (org+user) was active at the moment the
  // user actually closed the modal, so the deferred-write effect below can
  // detect an org switch that happened while the write was still pending —
  // see that effect's comment for why this matters.
  const closedScopeKeyRef = useRef<string | null>(null);
  // What the modal was showing when it closed. The write below can be deferred until
  // preferences hydrate, and flags may load or change meanwhile, so it records this snapshot
  // rather than whatever is visible by then, which the user never saw.
  const closedSnapshotRef = useRef<{ latestId: string | undefined; gatedIds: string[] }>({
    latestId: undefined,
    gatedIds: [],
  });

  useEffect(() => {
    if (!wasOpenRef.current && open) {
      // Just transitioned closed -> open: record the scope active right now
      // (read by the forced-close effect above, declared earlier so it
      // still sees this ref's PRE-update value on this very render — see
      // openedScopeKeyRef's own comment for why that ordering matters).
      openedScopeKeyRef.current = scopeKey;
    }
    if (wasOpenRef.current && !open) {
      // Codex P2, PR #6347, round 7: reset openedScopeKeyRef on EVERY close
      // (forced or genuine), not just leave it holding whichever scope this
      // session opened under. Without this, closing in org A and later
      // opening again in org B (dialog closed the whole time in between)
      // would have the forced-close effect read the stale "org A" capture
      // against the new render's "org B" scopeKey and immediately
      // force-close a dialog that just legitimately opened — the null guard
      // there exists precisely so a fresh session's first open isn't read
      // as an in-session scope change, and only a null openedScopeKeyRef
      // satisfies it.
      openedScopeKeyRef.current = null;
      if (forcedCloseRef.current) {
        // A suppression closure, not a user-driven one — don't mark seen,
        // just consume the flag so it doesn't affect the next real close.
        forcedCloseRef.current = false;
      } else {
        setJustClosed(true);
        closedScopeKeyRef.current = scopeKey;
        closedSnapshotRef.current = {
          latestId: visibleChangelog.latestId,
          gatedIds: visibleChangelog.gatedIds,
        };
      }
    }
    wasOpenRef.current = open;
  }, [open, scopeKey, visibleChangelog.latestId, visibleChangelog.gatedIds]);

  // Performs the deferred "seen" write once the modal has actually closed.
  // Guarded on preferencesReady (and re-run when it flips) because
  // usePersonalizationPreferences' applyUpdate silently no-ops writes made
  // before hydration finishes — a click on the entry-point button can open
  // (and be closed again) before that hydration completes, and without
  // preferencesReady in the deps this effect would never re-fire once it
  // does, permanently dropping the "seen" write instead of merely deferring
  // it. `justClosed` stays true across renders until the write actually
  // succeeds, so hydration finishing later still triggers it.
  useEffect(() => {
    const { latestId, gatedIds } = closedSnapshotRef.current;
    if (!justClosed || !preferencesReady || latestId === undefined) return;
    // The user may have switched organizations while this write was still
    // pending — usePersonalizationPreferences rebinds to the new org's scope
    // as soon as that happens, and this effect would otherwise fire once
    // hydration for the NEW scope completes, marking the new org's changelog
    // seen even though the actual close happened while viewing the old one.
    // Each org tracks "seen" independently by design, so a close in one org
    // must never write into a different org's scope — discard the pending
    // write (without persisting anything) rather than let it land here.
    if (closedScopeKeyRef.current !== scopeKey) {
      setJustClosed(false);
      return;
    }
    // Never regress: the entry-point button can be opened manually even when
    // hasUnseen is false (e.g. browsing history), so this write isn't always
    // reached only via the hasUnseen-gated auto-popup path — a stale client
    // build (older latest visible id than what's already stored, from a
    // more up-to-date device) must not overwrite a newer value on close.
    // isChangelogIdNewer, not a raw `>` — see its own doc comment for why a
    // plain string comparison breaks on a double-digit same-day suffix.
    if (
      preferences.lastSeenChangelogId === undefined ||
      isChangelogIdNewer(latestId, preferences.lastSeenChangelogId)
    ) {
      setLastSeenChangelogId(latestId);
    }
    // Flag-gated entries shown in this modal count as seen in this browser too.
    if (scopeKey !== null) markGatedSeen(scopeKey, gatedIds);
    setJustClosed(false);
  }, [
    justClosed,
    preferencesReady,
    setLastSeenChangelogId,
    scopeKey,
    preferences.lastSeenChangelogId,
  ]);

  const handleShareFeedback = useCallback(() => {
    setOpen(false);
    window.dispatchEvent(
      new CustomEvent("open-solomon", {
        detail: { prompt: "I'd like to share feedback about a recent change." },
      }),
    );
  }, [setOpen]);

  // The CTA dispatches "open-solomon", but the only listener for that event
  // (and the only mount of <SolomonPanel>) lives inside AppLayout.tsx —
  // hide the CTA rather than show a button that silently does nothing when
  // no AppLayout instance is actually mounted to receive it (see
  // useAppLayoutMounted above for why this is a live check rather than a
  // route list).
  const canShareFeedback = solomonEnabled && appLayoutMounted;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-[560px]" onCloseAutoFocus={(event) => {
        if (!menuReturnFocus.current) return;
        const target = [menuReturnFocus.current, buttonReturnFocus.current].find((element) => {
          if (!element?.isConnected || element.matches(":disabled,[aria-disabled='true']")) return false;
          const rect = element.getBoundingClientRect();
          return rect.width > 0 && rect.height > 0 && getComputedStyle(element).visibility === "visible";
        });
        menuReturnFocus.current = null;
        if (target) { event.preventDefault(); target.focus(); }
      }}>
        <DialogHeader>
          <DialogTitle>What's new in Forge</DialogTitle>
          <DialogDescription>Recent improvements, in case you missed them.</DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-2">
          {visibleChangelog.entries.map((entry) => (
            <div key={entry.id} className="space-y-2">
              <div className="flex items-baseline justify-between gap-2">
                <h3 className="font-medium">{entry.title}</h3>
                <span className="text-xs text-muted-foreground whitespace-nowrap">{entry.date}</span>
              </div>
              <ul className="space-y-1 text-sm text-muted-foreground">
                {entry.items.map((item, index) => (
                  <li key={index} className="flex items-start gap-2">
                    <span
                      className={cn(
                        "mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full",
                        item.type === "fix" ? "bg-amber-500" : "bg-primary",
                      )}
                    />
                    <span>{item.text}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {canShareFeedback && (
          <div className="flex items-center justify-between gap-3 pt-4 border-t">
            <p className="text-sm text-muted-foreground">Have feedback or found a bug?</p>
            <Button variant="outline" size="sm" onClick={handleShareFeedback}>
              Share feedback
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

/** Persistent top-bar entry point: opens the changelog on demand and shows a
 * dot badge while there's an entry the user hasn't seen yet.
 *
 * Expects a `ChangelogProvider` ancestor (mounted once, in `App.tsx`) to
 * actually render anything — it reads the context directly (rather than via
 * the throwing `useChangelogContext()`) and silently renders nothing when
 * that provider isn't present, since this button is rendered from many
 * places (including tests) that don't always mount the provider.
 *
 * Also renders nothing when `scopeKey` is null (no organization at all —
 * e.g. a platform super admin on an /admin/* page, which does render
 * AppLayout). ChangelogModal treats that the same as an unsuitable route and
 * fully suppresses itself (see its own `scopeKey === null` check), so
 * without this the button would show — with a misleading unseen badge, since
 * the null-scope hook reports ready defaults — and clicking it would open
 * the modal for one render only to have its own suppression effect close it
 * again immediately: a visibly broken entry point instead of simply absent. */
export function ChangelogEntryPointButton({
  presentation = "button",
  onMenuSelect,
}: {
  presentation?: "button" | "menu-item";
  /** The containing menu can finish its focus handoff before opening the dialog. */
  onMenuSelect?: (open: () => void) => void;
} = {}) {
  const ctx = useContext(ChangelogContext);
  const { preferences, isReady, scopeKey } = usePersonalizationPreferences();
  const currentScopeKey = useRef(scopeKey);
  currentScopeKey.current = scopeKey;
  const visibleChangelog = useVisibleChangelog(scopeKey);
  const hasUnseen =
    isReady &&
    (isEntryUnseen(visibleChangelog.latestId, preferences.lastSeenChangelogId) || visibleChangelog.hasUnseenGated);

  if (!ctx || scopeKey === null) return null;

  if (presentation === "menu-item") {
    return (
      <DropdownMenuItem
        aria-label={hasUnseen ? "What's new (unseen updates)" : "What's new"}
        onSelect={() => {
          const open = () => {
            if (currentScopeKey.current !== scopeKey) return;
            ctx.menuReturnFocus.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
            ctx.setOpen(true);
          };
          if (onMenuSelect) onMenuSelect(open); else open();
        }}
      >
        <Gift className="h-4 w-4" />
        What's new
        {hasUnseen && <span aria-hidden="true" className="ml-auto h-2.5 w-2.5 rounded-full bg-primary" />}
      </DropdownMenuItem>
    );
  }

  return (
    <Button
      type="button"
      ref={ctx.buttonReturnFocus}
      variant="ghost"
      size="icon"
      className="relative h-9 w-9"
      onClick={() => ctx.setOpen(true)}
      aria-label={hasUnseen ? "What's new (unseen updates)" : "What's new"}
      title="What's new"
    >
      <Gift className="h-5 w-5" />
      {hasUnseen && (
        <span className="absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full bg-primary ring-2 ring-background" />
      )}
    </Button>
  );
}
